import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthResponse, UserDto } from '@shared/catalog';
import { api } from './api';
import { keys, storage } from './storage';

interface AuthState {
  user: UserDto | null;
  ready: boolean;
  login(email: string, password: string): Promise<UserDto>;
  register(email: string, password: string, displayName: string): Promise<UserDto>;
  acceptToken(res: AuthResponse): void;
  /** Replaces the signed-in user's data (e.g. after renaming); the token stays. */
  updateUser(user: UserDto): void;
  logout(): void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDto | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!storage.get(keys.authToken)) return setReady(true);
    api
      .get<{ user: UserDto }>('/auth/me')
      .then((r) => setUser(r.user))
      .catch(() => storage.remove(keys.authToken))
      .finally(() => setReady(true));
  }, []);

  const acceptToken = useCallback((res: AuthResponse) => {
    storage.set(keys.authToken, res.token);
    setUser(res.user);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      ready,
      acceptToken,
      updateUser: setUser,
      async login(email, password) {
        const res = await api.post<AuthResponse>('/auth/login', { email, password });
        acceptToken(res);
        return res.user;
      },
      async register(email, password, displayName) {
        const res = await api.post<AuthResponse>('/auth/register', { email, password, displayName });
        acceptToken(res);
        return res.user;
      },
      logout() {
        storage.remove(keys.authToken);
        setUser(null);
      },
    }),
    [user, ready, acceptToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** Where to go after signing in: only a path on this site (never another origin such as "//evil.example"). */
export function safeNext(value: string | null, fallback: string): string {
  return value && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\') ? value : fallback;
}
